import React, { ReactNode, useEffect, useState } from "react";
import { authUser } from "@origin-digital/event-dispatcher";
import { isNative } from "@origin-digital/platform-helpers";
import { logger } from "@origin-digital/reporting-client";
import { ICategoriesAPIResponse } from "../../shared/interface/categories";
import { IFetch, useDaxi } from "./services/useDaxi";

export interface IAppContext {
  categories: IFetch<ICategoriesAPIResponse>;
  config: IAppConfig;
  isAuthenticated: boolean;
  isNative: boolean;
}

export interface ISessionResponse {
  sessionId?: string;
  timeout?: number;
}

const initialFetch = {
  loading: true,
  error: false,
  data: undefined,
};

export const AppContext = React.createContext<IAppContext>({
  categories: initialFetch,
  isAuthenticated: false,
  isNative: false,
  config: {} as IAppConfig,
});

export const useAppContext = (): IAppContext => React.useContext(AppContext);

export const AppContextConsumer = AppContext.Consumer;

export const AppContextProvider = ({
  children,
  config,
}: {
  children: ReactNode;
  config: IAppConfig;
}): JSX.Element => {
  const [session, setSession] = useState<IFetch<ISessionResponse>>(
    initialFetch
  );

  const [isAuthenticated, setIsAuthenticated] = useState<
    IAppContext["isAuthenticated"]
  >(false);

  //fetch categories data
  const categories = useDaxi<ICategoriesAPIResponse>(
    "/api/intercomretail/help-support/categories"
  );

  useEffect(() => {
    let interval;
    if (session?.data?.sessionId && session?.data?.timeout) {
      interval = setInterval(() => {
        logger.debug("clearing out sessionId");
        setSession({
          ...session,
          data: { sessionId: undefined, timeout: undefined },
        });
      }, session.data.timeout * 1000);
    }

    return () => interval && clearInterval(interval);
  }, [session]);

  useEffect(() => {
    //set customer context based on whether the user is authed
    authUser({ auth0Environment: config.env as EnvironmentNames }).then(
      ({ isAuth }) => {
        const authed = !!isAuth;
        setIsAuthenticated(authed);
      }
    );
  }, []);

  return (
    <AppContext.Provider
      value={{
        categories,
        config,
        isAuthenticated,
        isNative: isNative(),
      }}
    >
      {children}
    </AppContext.Provider>
  );
};
